import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Loader2, ArrowLeft } from "lucide-react";
import "../Content/CSS/Profilepage.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:50574";

export default function HotelBookings({ userSession }) {
  const navigate = useNavigate();

  // UserId / Userkey for booking API
  const userId =
    userSession?.UserId ||
    userSession?.Userkey ||
    userSession?.UserKey ||
    localStorage.getItem("userKey") ||
    "";

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchBookings = useCallback(async () => {
    if (!fromDate || !toDate) {
      setError("Please select both From Date and To Date.");
      return;
    }
    if (fromDate > toDate) {
      setError("From Date cannot be greater than To Date.");
      return;
    }
    if (!userId) {
      setError("User ID is missing. Please log in again.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/hotel/GetBookingHistory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          FromDate: fromDate,
          ToDate: toDate,
          UserId: userId,
        }),
      });

      const json = await res.json();

      if (!res.ok || json.status === "error") {
        setError(json.message || "Failed to fetch booking history.");
        setBookings([]);
        return;
      }

      setBookings(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      console.error("GetBookingHistory error:", err);
      setError("Network error. Please try again.");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, userId]);

  return (
    <div className="profile-page">
      <div className="profile-page__wrapper">
        <main className="profile-main">
          <div className="profile-main__header">
            <h1>Hotel Booking Summary</h1>
          </div>

          <div className="booking-filters">
            <div className="booking-filters__field">
              <label htmlFor="hotelFromDate">From Date</label>
              <div className="date-input-wrap">
                <Calendar size={16} />
                <input
                  id="hotelFromDate"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
            </div>

            <div className="booking-filters__field">
              <label htmlFor="hotelToDate">To Date</label>
              <div className="date-input-wrap">
                <Calendar size={16} />
                <input
                  id="hotelToDate"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>

            <button
              type="button"
              className="booking-filters__btn"
              onClick={fetchBookings}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="spin" /> Loading...
                </>
              ) : (
                "FILTER NOW"
              )}
            </button>
          </div>

          {error && <div className="booking-error">{error}</div>}

          <div className="booking-tabs">
            <button type="button" className="booking-tabs__item active">
              ALL BOOKINGS
            </button>
          </div>

          <div className="booking-list">
            {loading ? (
              <div className="booking-empty">
                <Loader2 size={24} className="spin" />
                <p>Loading bookings...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="booking-empty">
                <p>No hotel bookings found.</p>
              </div>
            ) : (
              bookings.map((b, idx) => (
                <div key={b.TransactionId ?? idx} className="booking-row">
                  <div className="booking-row__main">
                    <div className="booking-row__info">
                      <h3 className="booking-row__title">{b.HotelName || "—"}</h3>
                      <p className="booking-row__address">{b.HotelAddress || "—"}</p>
                      <div className="booking-row__meta">
                        <span>
                          Rooms: <strong>{b.NumberOfRooms ?? "—"}</strong>
                        </span>
                        <span>
                          Adults: <strong>{b.NumberOfAdults ?? "—"}</strong>
                        </span>
                        <span>
                          Children: <strong>{b.NumberOfChild ?? "—"}</strong>
                        </span>
                      </div>
                      <div className="booking-row__meta">
                        <span>
                          Booking Date: <strong>{b.BookingDate || "—"}</strong>
                        </span>
                        <span>
                          Txn ID: <strong>{b.TransactionId ?? "—"}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="booking-row__customer">
                    <p>
                      <strong>{b.CustomerName || "—"}</strong>
                    </p>
                    <p>{b.CustomerEmail || "—"}</p>
                    <p>{b.CustomerMobile || "—"}</p>
                  </div>
                </div>
              ))
            )}

            <button
              type="button"
              className="modal-btn modal-btn--outline"
              style={{ marginTop: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={14} /> Back
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}